import React, { useState, useEffect } from 'react'
import Editor from '@monaco-editor/react'
import { PlayIcon, CheckCircleIcon, XCircleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { executeCode } from '../store/slices/testSlice'
import { sandboxAPI, assessmentAPI } from '../services/api'
import clsx from 'clsx'
import toast from 'react-hot-toast'

interface TestCase {
  input: string
  expected_output: string
  test_case_id: number
}

interface ExecutionResult {
  question_id: number
  language: string
  source_code: string
  score: number
  max_score: number
  execution_result: {
    language: string
    total_test_cases: number
    passed_test_cases: number
    failed_test_cases: number
    success_rate: number
    test_results?: Array<{
      test_case_id: number
      input: string
      expected_output: string
      actual_output: string
      passed: boolean
      execution_time?: number
      memory_used?: number
      error?: string
    }>
    // New structured format fields
    example_results?: Array<{
      test_case_id: number
      inputs: Array<{ name: string; value: any; data_type: string }>
      expected_output: { value: any; data_type: string }
      actual_output: string
      passed: boolean
      execution_time_ms?: number
      memory_used_kb?: number
      status: any
      error?: string
      explanation?: string
    }>
    hidden_results_summary?: {
      total: number
      passed: number
      failed: number
      score_percentage: number
    }
    overall_status: string
    feedback?: string
  }
  status: string
  error?: string
}

interface CodeEditorProps {
  questionId: number
  sessionId: string
  language: string
  onLanguageChange: (language: string) => void
  starterCode?: string | Record<string, string>
  testCases?: TestCase[]
  onCodeChange?: (code: string) => void
  isReadOnly?: boolean
  // Props for submitting code
  assessmentId?: string
  candidateId?: number
  driveId?: number
  onCodeSubmitted?: (questionId: number) => void
}

const SUPPORTED_LANGUAGES = [
  { value: 'python', label: 'Python 3', defaultCode: '# Write your solution here\ndef solution():\n    pass\n' },
  { value: 'java', label: 'Java', defaultCode: 'public class Solution {\n    public static void main(String[] args) {\n        // Write your solution here\n    }\n}' },
  { value: 'cpp', label: 'C++', defaultCode: '#include <iostream>\nusing namespace std;\n\nint main() {\n    // Write your solution here\n    return 0;\n}' },
  { value: 'javascript', label: 'JavaScript', defaultCode: '// Write your solution here\nfunction solution() {\n    \n}\n' },
  { value: 'typescript', label: 'TypeScript', defaultCode: '// Write your solution here\nfunction solution(): void {\n    \n}\n' },
]

const CodeEditor: React.FC<CodeEditorProps> = ({
  questionId,
  sessionId,
  language,
  onLanguageChange,
  starterCode,
  testCases = [],
  onCodeChange,
  isReadOnly = false,
  assessmentId,
  candidateId,
  driveId,
  onCodeSubmitted,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [code, setCode] = useState<string>(typeof starterCode === 'string' ? starterCode : '')
  const [isExecuting, setIsExecuting] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lastSubmittedAt, setLastSubmittedAt] = useState<Date | null>(null)
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null)
  const [selectedTab, setSelectedTab] = useState<'code' | 'results'>('code')
  const [sandboxStatus, setSandboxStatus] = useState<'checking' | 'connected' | 'error'>('checking')

  // Initialize code when language changes
  useEffect(() => {
    const langConfig = SUPPORTED_LANGUAGES.find(lang => lang.value === language)
    if (langConfig) {
      // Always switch to the appropriate code for the selected language
      if (starterCode && typeof starterCode === 'object' && starterCode[language]) {
        // Use starter code for this specific language
        setCode(starterCode[language] || '')
      } else if (typeof starterCode === 'string') {
        // Use string starter code
        setCode(starterCode)
      } else {
        // Use default code for the language
        setCode(langConfig.defaultCode)
      }
      
      // Clear execution results when switching languages
      setExecutionResult(null)
    }
  }, [language, starterCode])

  // Update parent component when code changes
  useEffect(() => {
    if (typeof code === 'string') {
      onCodeChange?.(code)
    }
  }, [code, onCodeChange])

  // Check sandbox status on mount
  useEffect(() => {
    const checkSandboxStatus = async () => {
      try {
        console.log('Checking sandbox health...')
        // Use the health endpoint for status check
        const response = await sandboxAPI.healthCheck()
        console.log('Health check response:', response.data)
        if (response.data.status === 'healthy') {
          console.log('Setting sandbox status to connected')
          setSandboxStatus('connected')
        } else if (response.data.status === 'degraded') {
          console.log('Setting sandbox status to error (degraded)')
          setSandboxStatus('error') // Judge0 offline but service available
        } else {
          console.log('Setting sandbox status to error (unhealthy)')
          setSandboxStatus('error')
        }
      } catch (error) {
        console.error('Sandbox health check failed:', error)
        setSandboxStatus('error')
      }
    }
    
    checkSandboxStatus()
  }, [])

  const handleRunCode = async () => {
    if (typeof code !== 'string' || !code.trim()) {
      // Code validation is handled in the Results tab - no pop-up messages
      return
    }

    setIsExecuting(true)
    setSelectedTab('results')

    try {
      const response = await dispatch(executeCode({
        sessionId,
        questionId,
        sourceCode: typeof code === 'string' ? code : '',
        language,
      }))

      if (response.payload) {
        setExecutionResult(response.payload as ExecutionResult)
        
        const result = response.payload as ExecutionResult
        const passedTests = result.execution_result.passed_test_cases
        const totalTests = result.execution_result.total_test_cases
        
        // Test case results are now shown in the Results tab only - no pop-up messages
      }
    } catch (error) {
      console.error('Code execution failed:', error)
      
      // Error details are shown in the Results tab - no pop-up messages
    } finally {
      setIsExecuting(false)
    }
  }

  const handleSubmitCode = async () => {
    if (typeof code !== 'string' || !code.trim()) {
      toast.error('Please write some code before submitting')
      return
    }

    if (!assessmentId || !candidateId || !driveId) {
      toast.error('Missing assessment information. Cannot submit code.')
      console.error('Missing props for code submission:', { assessmentId, candidateId, driveId })
      return
    }

    setIsSubmitting(true)
    setSelectedTab('results')

    try {
      // First, run the code (same as Run Code) to get execution results
      const executeResponse = await dispatch(executeCode({
        sessionId,
        questionId,
        sourceCode: code,
        language,
      }))

      // If execution was successful, display the results
      if (executeResponse.payload) {
        setExecutionResult(executeResponse.payload as ExecutionResult)
      }

      // Then, save the response separately using the execution result we already have
      // This ensures we save the same data that's displayed on screen
      try {
        const executionResult = executeResponse.payload as ExecutionResult
        await assessmentAPI.submitCodingSolution(assessmentId, {
          candidate_id: candidateId,
          drive_id: driveId,
          question_id: String(questionId),
          source_code: code,
          language: language,
          execution_result: executionResult.execution_result, // Pass the execution result
        })
        
        setLastSubmittedAt(new Date())
        toast.success('Code submitted! You can submit again if you improve your solution. Highest score will be saved.')

        // Notify parent component
        if (onCodeSubmitted) {
          onCodeSubmitted(questionId)
        }
      } catch (saveError: any) {
        // If save fails, still show execution results but warn user
        console.error('Failed to save code submission:', saveError)
        toast.error('Code executed but failed to save. Please try again.')
      }
    } catch (error: any) {
      console.error('Code execution failed:', error)
      toast.error('Failed to execute code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getMonacoLanguage = (lang: string): string => {
    const langMap: Record<string, string> = {
      python: 'python',
      java: 'java',
      cpp: 'cpp',
      javascript: 'javascript',
      typescript: 'typescript',
    }
    return langMap[lang] || 'plaintext'
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-4">
          {/* Sandbox status indicator */}
          <div className="flex items-center space-x-2">
            <div className={clsx(
              'w-2 h-2 rounded-full',
              sandboxStatus === 'connected' ? 'bg-green-500' : 
              sandboxStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'
            )} />
            <span className="text-xs text-gray-600">
              {sandboxStatus === 'connected' ? 'Compiler Ready' : 
               sandboxStatus === 'error' ? 'Compiler Offline' : 'Checking...'}
            </span>
          </div>
          
          {/* Language selector */}
          <select
            value={language}
            onChange={(e) => onLanguageChange(e.target.value)}
            disabled={isReadOnly}
            className="input w-auto"
          >
            {(() => {
              // Only show languages that have starter code templates
              const availableLanguages = starterCode && typeof starterCode === 'object' 
                ? Object.keys(starterCode).filter(lang => starterCode[lang] && starterCode[lang].trim())
                : SUPPORTED_LANGUAGES.map(lang => lang.value);
              
              return availableLanguages.map((langValue) => {
                const langConfig = SUPPORTED_LANGUAGES.find(lang => lang.value === langValue);
                return (
                  <option key={langValue} value={langValue}>
                    {langConfig?.label || langValue}
              </option>
                );
              });
            })()}
          </select>
          
          {/* Tabs */}
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedTab('code')}
              className={clsx(
                'px-3 py-1 text-sm rounded-md',
                selectedTab === 'code' 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'text-gray-600 hover:text-gray-800'
              )}
            >
              Code
            </button>
            <button
              onClick={() => setSelectedTab('results')}
              className={clsx(
                'px-3 py-1 text-sm rounded-md',
                selectedTab === 'results' 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'text-gray-600 hover:text-gray-800'
              )}
            >
              Results
              {executionResult && (
                <span className={clsx(
                  'ml-1 inline-block w-2 h-2 rounded-full',
                  executionResult.execution_result.overall_status === 'PASSED' 
                    ? 'bg-success-500' 
                    : 'bg-danger-500'
                )} />
              )}
            </button>
          </div>
        </div>

        {/* Run and Submit buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRunCode}
            disabled={isExecuting || isSubmitting || isReadOnly || sandboxStatus === 'error'}
            className={clsx(
              'btn btn-outline flex items-center justify-center space-x-1.5 px-3 py-1.5 text-sm rounded-md',
              (isExecuting || isSubmitting || sandboxStatus === 'error') && 'opacity-50 cursor-not-allowed'
            )}
            title={sandboxStatus === 'error' ? 'Compiler is offline. Please check your connection.' : ''}
          >
            {isExecuting ? (
              <div className="spinner w-3.5 h-3.5" />
            ) : (
              <PlayIcon className="w-3.5 h-3.5" />
            )}
            <span>
              {isExecuting ? 'Running...' : 
               sandboxStatus === 'error' ? 'Compiler Offline' : 'Run Code'}
            </span>
          </button>

          {assessmentId && candidateId && driveId && (
            <button
              onClick={handleSubmitCode}
              disabled={isSubmitting || isExecuting || isReadOnly || !(typeof code === 'string' && code.trim())}
              className={clsx(
                'btn btn-primary flex items-center justify-center space-x-1.5 px-3 py-1.5 text-sm rounded-md',
                lastSubmittedAt && 'bg-success-600 hover:bg-success-700',
                (isSubmitting || isExecuting || isReadOnly || !code.trim()) && 'opacity-50 cursor-not-allowed'
              )}
              title={lastSubmittedAt ? 'Code submitted - you can submit again to improve your score (highest will be saved)' : 'Submit this code - you can submit multiple times, highest score will be saved'}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner w-3.5 h-3.5" />
                  <span>Submitting...</span>
                </>
              ) : lastSubmittedAt ? (
                <>
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  <span>Submit Again</span>
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-3.5 h-3.5" />
                  <span>Submit Code</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        {selectedTab === 'code' ? (
          <div className="flex-1">
            <Editor
              key={language} // Force re-render when language changes
              height="100%"
              language={getMonacoLanguage(language)}
              value={typeof code === 'string' ? code : ''}
              onChange={(value) => setCode(value || '')}
              options={{
                readOnly: isReadOnly,
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                theme: 'vs-light',
                wordWrap: 'on',
                tabSize: 2,
                insertSpaces: true,
              }}
            />
          </div>
        ) : (
          <div className="flex-1 p-4 overflow-auto">
            {!executionResult ? (
              <div className="text-center text-gray-500 py-8">
                <PlayIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Run your code to see the results</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Overall result */}
                <div className={clsx(
                  'p-4 rounded-lg border',
                  executionResult.execution_result.overall_status === 'PASSED'
                    ? 'bg-success-50 border-success-200'
                    : executionResult.execution_result.overall_status === 'COMPILATION_ERROR'
                    ? 'bg-orange-50 border-orange-200'
                    : executionResult.execution_result.overall_status === 'RUNTIME_ERROR'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-danger-50 border-danger-200'
                )}>
                  <div className="flex items-center space-x-2">
                    {executionResult.execution_result.overall_status === 'PASSED' ? (
                      <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    ) : executionResult.execution_result.overall_status === 'COMPILATION_ERROR' ? (
                      <XCircleIcon className="w-5 h-5 text-orange-600" />
                    ) : executionResult.execution_result.overall_status === 'RUNTIME_ERROR' ? (
                      <XCircleIcon className="w-5 h-5 text-red-600" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-danger-600" />
                    )}
                    <span className="font-semibold">
                      {executionResult.execution_result.overall_status === 'COMPILATION_ERROR' 
                        ? 'Compilation Error'
                        : executionResult.execution_result.overall_status === 'RUNTIME_ERROR'
                        ? 'Runtime Error'
                        : `${executionResult.execution_result.passed_test_cases}/${executionResult.execution_result.total_test_cases} test cases passed`
                      }
                    </span>
                    <span className="text-sm text-gray-600">
                      (Score: {executionResult.score}/{executionResult.max_score})
                    </span>
                  </div>
                  
                  {/* Show error message if present */}
                  {executionResult.error && (
                    <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-800">
                      <strong>Error:</strong> {executionResult.error}
                    </div>
                  )}
                </div>

                {/* Test case results */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Test Results</h3>
                  
                  {/* Show feedback if available */}
                  {executionResult.execution_result.feedback && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">{executionResult.execution_result.feedback}</p>
                    </div>
                  )}
                  
                  {/* Example test cases (visible to candidate) */}
                  {executionResult.execution_result.example_results && executionResult.execution_result.example_results.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-800 mb-3">Example Test Cases</h4>
                      <div className="space-y-3">
                        {executionResult.execution_result.example_results.map((result, index) => (
                          <div
                            key={result.test_case_id}
                            className={clsx(
                              'p-4 rounded-lg border',
                              result.passed 
                                ? 'bg-success-50 border-success-200' 
                                : 'bg-danger-50 border-danger-200'
                            )}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium">Example {index + 1}</span>
                              <div className="flex items-center space-x-2">
                                {result.passed ? (
                                  <CheckCircleIcon className="w-5 h-5 text-success-600" />
                                ) : (
                                  <XCircleIcon className="w-5 h-5 text-danger-600" />
                                )}
                                <span className="text-sm text-gray-600">
                                  {result.execution_time_ms || 0}ms
                                </span>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <div className="text-gray-600 mb-1">Input:</div>
                                <div className="font-mono bg-white border border-gray-200 p-3 rounded">
                                  {result.inputs.map((input, idx) => (
                                    <div key={idx} className="mb-1">
                                      <span className="text-blue-600 font-medium">{input.name}</span> = {JSON.stringify(input.value)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 mb-1">Expected Output:</div>
                                <div className="font-mono bg-white border border-gray-200 p-3 rounded">
                                  {JSON.stringify(result.expected_output.value)}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 mb-1">Your Output:</div>
                                <div className={clsx(
                                  'font-mono p-3 rounded border',
                                  result.passed ? 'bg-success-100 border-success-200' : 'bg-danger-100 border-danger-200'
                                )}>
                                  {result.actual_output || '(empty)'}
                                </div>
                              </div>
                              {result.error && (
                                <div>
                                  <div className="text-gray-600 mb-1">Error:</div>
                                  <div className="font-mono bg-red-100 border border-red-200 p-3 rounded text-red-800">
                                    {result.error}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {result.explanation && (
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                <div className="text-sm text-blue-800">
                                  <span className="font-medium">Explanation:</span> {result.explanation}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Hidden test cases summary - temporarily disabled due to type issues */}
                  {/* {executionResult.execution_result.hidden_test_cases && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">Hidden Test Cases</h4>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {executionResult.execution_result.hidden_test_cases.passed}/{executionResult.execution_result.hidden_test_cases.total} passed
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {executionResult.execution_result.hidden_test_cases.total > 0 
                            ? ((executionResult.execution_result.hidden_test_cases.passed / executionResult.execution_result.hidden_test_cases.total) * 100).toFixed(0)
                            : 0}% Score
                        </span>
                      </div>
                    </div>
                  )} */}
                  
                  {/* Legacy hidden test cases summary */}
                  {executionResult.execution_result.hidden_results_summary && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">Hidden Test Cases</h4>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {executionResult.execution_result.hidden_results_summary.passed}/{executionResult.execution_result.hidden_results_summary.total} passed
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {executionResult.execution_result.hidden_results_summary.score_percentage.toFixed(1)}% score
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Legacy test results format (fallback) */}
                  {executionResult.execution_result.test_results && executionResult.execution_result.test_results.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-800 mb-3">Test Cases</h4>
                      <div className="space-y-3">
                  {executionResult.execution_result.test_results.map((result, index) => (
                    <div
                      key={result.test_case_id}
                      className={clsx(
                        'p-3 rounded border',
                        result.passed 
                          ? 'bg-success-50 border-success-200' 
                          : 'bg-danger-50 border-danger-200'
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Test Case {result.test_case_id}</span>
                        <div className="flex items-center space-x-2">
                          {result.passed ? (
                            <CheckCircleIcon className="w-4 h-4 text-success-600" />
                          ) : (
                            <XCircleIcon className="w-4 h-4 text-danger-600" />
                          )}
                          <span className="text-sm">
                            {result.execution_time}ms
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Input:</div>
                          <div className="font-mono bg-gray-100 p-2 rounded">
                            {result.input || '(empty)'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Expected:</div>
                          <div className="font-mono bg-gray-100 p-2 rounded">
                            {result.expected_output}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Your Output:</div>
                          <div className={clsx(
                            'font-mono p-2 rounded',
                            result.passed ? 'bg-success-100' : 'bg-danger-100'
                          )}>
                            {result.actual_output || '(empty)'}
                          </div>
                        </div>
                        {result.error && (
                          <div>
                            <div className="text-gray-600">Error:</div>
                            <div className="font-mono bg-danger-100 p-2 rounded text-danger-800">
                              {result.error}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CodeEditor
